import { cn } from '@kit/ui/utils';

import { pairsFor } from '~/config/subscription-tiers.config';
import { formatDays } from '~/lib/plans/pilot';
import {
  type PriceType,
  formatAmount,
  formatPln,
  grossFromNet,
} from '~/lib/plans/plan-price';

import { Features } from './pricing-features';

/** Dates are pre-formatted (dd.MM.yyyy) on the server to avoid TZ mismatches. */
export type DedicatedPlanStatus =
  | { type: 'active'; endsAt: string | null }
  | { type: 'pilot-waiting'; days: number }
  | { type: 'expired' }
  | { type: 'trial'; endsAt: string }
  | { type: 'trial-expired' };

export interface DedicatedPlan {
  name: string;
  maxDrugs: number;
  monthlyChecks: number | null;
  price: number | null;
  priceType: PriceType;
  status: DedicatedPlanStatus;
}

const STATUS_DISPLAY: Record<
  DedicatedPlanStatus['type'],
  { dot: string; label: string }
> = {
  active: { dot: 'bg-green-500', label: 'Subskrypcja aktywna' },
  'pilot-waiting': { dot: 'bg-green-500', label: 'Pilotaż' },
  expired: { dot: 'bg-red-500', label: 'Subskrypcja wygasła' },
  trial: { dot: 'bg-blue-500', label: 'Okres próbny' },
  'trial-expired': { dot: 'bg-red-500', label: 'Okres próbny zakończony' },
};

export function DedicatedPlanCard({ plan }: { plan: DedicatedPlan }) {
  const { status } = plan;
  const display = STATUS_DISPLAY[status.type];
  const endsAt =
    status.type === 'active' || status.type === 'trial' ? status.endsAt : null;

  return (
    <div className="relative flex flex-col rounded-xl border-2 border-zinc-900 bg-[#fffef9] p-6 shadow-[3px_3px_0_0_#303030]">
      <h3 className="font-heading text-lg font-semibold text-slate-700">
        Plan dedykowany
      </h3>
      <p className="font-heading mt-0.5 text-xs text-slate-500 italic">
        Indywidualne warunki
      </p>
      <p className="mt-3 text-2xl leading-tight font-black text-balance break-words text-zinc-900">
        „{plan.name}”
      </p>

      <Features
        maxDrugs={plan.maxDrugs}
        pairs={pairsFor(plan.maxDrugs)}
        monthlyChecks={plan.monthlyChecks}
      />

      {plan.price !== null && (
        <PlanPrice price={plan.price} priceType={plan.priceType} />
      )}

      <div className="-mx-6 mt-auto -mb-6 pt-6">
        <div className="rounded-b-[9px] bg-zinc-900 px-4 py-2.5">
          <p className="flex items-center justify-center gap-2 text-xs font-medium text-white">
            <span
              className={cn('h-1.5 w-1.5 shrink-0 rounded-full', display.dot)}
            />
            {display.label}
            {endsAt && <span className="text-zinc-400">· do {endsAt}</span>}
            {status.type === 'pilot-waiting' && (
              <span className="text-zinc-400">· {formatDays(status.days)}</span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

/** Net: "149 zł/mies. (183,27 zł z VAT)"; gross: "199,50 zł/mies. (z VAT)". */
function PlanPrice({
  price,
  priceType,
}: {
  price: number;
  priceType: PriceType;
}) {
  const grossNote =
    priceType === 'net' ? `${formatPln(grossFromNet(price))} z VAT` : 'z VAT';

  return (
    <p className="mt-5 flex flex-wrap items-baseline gap-x-1 border-t border-dashed border-slate-200 pt-4">
      <span className="text-xl font-bold text-slate-800">
        {formatAmount(price)}
      </span>
      <span className="text-sm text-slate-500">zł/mies.</span>
      <span className="ml-1 text-xs text-slate-400">({grossNote})</span>
    </p>
  );
}
